import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Divider,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Card,
  CardContent,
  LinearProgress,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  Button,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableContainer,
  AppBar,
  Toolbar,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  TextField,
  Menu,
  MenuItem,
  alpha,
} from "@mui/material";
import {
  TrendingUp,
  CheckCircle,
  Warning,
  Schedule,
  Payment,
  Autorenew,
  Analytics,
  InfoOutlined,
  ErrorOutline,
  AssignmentTurnedIn,
  Receipt,
  CompareArrows,
  PendingActions,
  Download,
  Refresh,
  FilterList,
  Visibility,
  Person,
  AccessTime,
  Menu as MenuIcon,
  Dashboard,
  AccountCircle,
  Notifications,
  Search,
  Brightness4,
  Brightness7,
  BarChart,
  PieChart,
  WarningAmber,
} from "@mui/icons-material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Legend,
  BarChart as RechartsBarChart,
  Bar,
} from "recharts";
import axios from "axios";

/** ---------------- Helpers ---------------- */
const toNum = (v) => {
  if (v === null || v === undefined) return 0;
  const s = String(v)
    .replace(/[,₹\s]/g, "")
    .trim();
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

const toINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number.isFinite(n) ? n : 0);

const normKey = (s = "") => s.toLowerCase().replace(/\s+/g, "_");

/** Sum same part_no across multiple rows (e.g., 630003 split lines) */
function aggregateByPart(items, pick) {
  const m = new Map();
  for (const it of items) {
    const part =
      it.part_no || it.item_code || it.code || (it.part && it.part.code) || "";
    if (!part) continue;

    const key = String(part).trim();
    const prev = m.get(key) || 0;
    m.set(key, prev + pick(it));
  }
  return m;
}

/** Pickers for each document type (handle field variants) */
function poPickers(poItems) {
  return {
    qtyByPart: aggregateByPart(poItems, (it) =>
      toNum(
        it["qty."] ??
          it.qty ??
          it.quantity ??
          it.parsed_qty ??
          it["parsed_qty."]
      )
    ),
    priceByPart: aggregateByPart(poItems, (it) =>
      toNum(it.parsed_rate ?? it.rate ?? it.unit_price)
    ),
    hsnByPart: (() => {
      const m = new Map();
      for (const it of poItems) {
        const part = it.part_no?.toString().trim();
        if (!part) continue;
        if (!m.has(part))
          m.set(part, (it.hsn_code ?? it.hsn ?? "").toString().trim());
      }
      return m;
    })(),
  };
}

// function grnPickers(grnItems) {
//   return {
//     qtyByPart: aggregateByPart(grnItems, (it) =>
//       toNum(it.received_q ?? it.qty ?? it.parsed_qty)
//     ),
//   };
// }
function grnPickers(grnItems) {
  return {
    qtyByPart: aggregateByPart(grnItems, (it) =>
      toNum(
        it.received_quantity ??
          it.parsed_received_quantity ??
          it.received_q ??
          it.qty ??
          it.parsed_qty
      )
    ),
  };
}

function invPickers(invItems) {
  return {
    qtyByPart: aggregateByPart(invItems, (it) =>
      toNum(
        it.qty ??
          it.quantity ??
          it.received_quantity ??
          it.parsed_qty ??
          it.parsed_quantity
      )
    ),
    priceByPart: aggregateByPart(invItems, (it) =>
      toNum(it.unit_price ?? it.rate ?? it.parsed_unit_price ?? it.parsed_rate)
    ),
    hsnByPart: (() => {
      const m = new Map();
      for (const it of invItems) {
        const part = it.part_no || it.code || (it.part && it.part.code) || "";
        if (!part) continue;
        if (!m.has(part))
          m.set(part, (it.hsn ?? it.hsn_code ?? "").toString().trim());
      }
      return m;
    })(),
  };
}

/** Reconcile one TWM record (one triad) -> mismatches & class */
function reconcileRecord(rec) {
  const rj = rec.result_json || {};
  const po = rj.po_data?.[0] ?? {};
  const grn = rj.grn_data?.[0] ?? {};
  const inv = rj.invoice_data?.[0] ?? {};

  const poItems =
    po.line_items?.map((x) => ({
      part_no: x.part_no,
      qty: x.qty ?? x["qty."],
      "qty.": x["qty."],
      parsed_qty: x.parsed_qty ?? x["parsed_qty."],
      rate: x.rate,
      parsed_rate: x.parsed_rate,
      hsn_code: x.hsn_code,
    })) ?? [];

  const grnItems =
    grn.line_items?.map((x) => ({
      part_no: x.part_no ?? x.item_code,
      received_q: x.received_quantity ?? x.received_q, // Fixed field name
      received_quantity: x.received_quantity,
      parsed_qty: x.parsed_received_quantity ?? x.parsed_qty,
    })) ?? [];

  const invItems =
    inv.line_items?.map((x) => ({
      part_no: x.part_no ?? x.code,
      qty: x.qty ?? x.quantity,
      parsed_qty: x.parsed_qty ?? x.parsed_quantity,
      unit_price: x.unit_price,
      parsed_unit_price: x.parsed_unit_price,
      hsn: x.hsn ?? x.hsn_code,
    })) ?? [];

  const {
    qtyByPart: poQty,
    priceByPart: poPrice,
    hsnByPart: poHSN,
  } = poPickers(poItems);
  const { qtyByPart: grnQty } = grnPickers(grnItems);
  const {
    qtyByPart: invQty,
    priceByPart: invPrice,
    hsnByPart: invHSN,
  } = invPickers(invItems);

  const poParts = new Set([...poQty.keys()]);
  const grnParts = new Set([...grnQty.keys()]);
  const invParts = new Set([...invQty.keys()]);

  const mismatches = [];
  const inc = (reason) => mismatches.push(reason);
  console.log(mismatches);
  // Missing / extra items
  for (const p of poParts) {
    if (!grnParts.has(p)) inc(`Missing in GRN: ${p}`);
    if (!invParts.has(p)) inc(`Missing in Invoice: ${p}`);
  }
  for (const p of grnParts) if (!poParts.has(p)) inc(`Extra in GRN: ${p}`);
  for (const p of invParts) if (!poParts.has(p)) inc(`Extra in Invoice: ${p}`);

  // Quantity / Price / HSN mismatches (only where present in all)
  for (const p of poParts) {
    const qPO = poQty.get(p) ?? 0;
    const qGRN = grnQty.get(p) ?? 0;
    const qINV = invQty.get(p) ?? 0;
    if (qPO !== qGRN || qPO !== qINV) {
      inc(`Quantity mismatch: ${p} (PO ${qPO} vs GRN ${qGRN} vs INV ${qINV})`);
    }

    const prPO = poPrice.get(p);
    const prINV = invPrice.get(p);
    if (Number.isFinite(prPO) && Number.isFinite(prINV) && prPO !== prINV) {
      inc(`Price mismatch: ${p} (PO ${toINR(prPO)} vs INV ${toINR(prINV)})`);
    }

    const hPO = poHSN.get(p);
    const hINV = invHSN.get(p);
    if (hPO && hINV && hPO !== hINV) {
      inc(`HSN mismatch: ${p} (PO ${hPO} vs INV ${hINV})`);
    }
  }

  // Invoice total & exception value
  const invAmount = toNum(inv.invoice_amount ?? inv.total_amount ?? inv.amount);

  // Classification rules:
  // - Fully Matched: no mismatches AND status APPROVED (or you can relax the status)
  // - Exceptions: any Price/Quantity mismatch or Missing/Extra items
  // - Partially Matched: only HSN/tax/meta style mismatches OR record is IN_REVIEW but math looks fine
  const hasQtyOrPriceOrItemIssue = mismatches.some((m) =>
    /Quantity mismatch|Price mismatch|Missing|Extra/.test(m)
  );

  let classification = "Fully Matched";
  if (hasQtyOrPriceOrItemIssue) classification = "With Exceptions";
  else if (
    mismatches.length > 0 ||
    rec.status === "IN_REVIEW" ||
    rec.status === "PENDING"
  )
    classification = "Partially Matched";

  const readyForPayment =
    rec.status === "APPROVED" && classification !== "With Exceptions";

  // Map mismatch reasons for Top-3
  const reasonBuckets = mismatches.map((m) => {
    if (m.startsWith("Quantity mismatch")) return "Quantity mismatch";
    if (m.startsWith("Price mismatch")) return "Price mismatch";
    if (m.startsWith("HSN mismatch")) return "Tax/HSN mismatch";
    if (m.startsWith("Missing")) return "Item Missing";
    if (m.startsWith("Extra")) return "Extra item";
    return "Meta mismatch";
  });

  return {
    classification,
    readyForPayment,
    invAmount,
    mismatches,
    reasonBuckets,
    createdAt: rec.created_at,
    hasException: classification === "With Exceptions",
    matchCode: rec.match_code,
    status: rec.status,
  };
}

/** Aggregate whole dataset -> KPIs */
function computeKPIs(data) {
  console.log(data);
  const per = data.map(reconcileRecord);

  console.log(per);
  const approved = per?.filter((status) => status.status === "APPROVED").length;
  const total = per.length;
  const fully = per.filter((x) => x.classification === "Fully Matched").length;
  const partial = per.filter(
    (x) => x.classification === "Partially Matched"
  ).length;
  const exceptions = per.filter((x) => x.classification === "With Exceptions");
  const withExceptions = exceptions.length;
  const readyPay = per.filter((x) => x.readyForPayment).length;

  const totalExceptionValue = exceptions.reduce(
    (s, x) => s + (x.invAmount || 0),
    0
  );

  // Aging > 7 days for exception cases
  const now = new Date();
  const agingCount = exceptions.filter((x) => {
    if (!x.createdAt) return false;
    const created = new Date(x.createdAt);
    const diffDays = (now - created) / (1000 * 60 * 60 * 24);
    return diffDays > 7;
  }).length;

  // Top 3 mismatch reasons
  const reasonCount = new Map();
  per.forEach((p) => {
    p.reasonBuckets.forEach((r) =>
      reasonCount.set(r, (reasonCount.get(r) || 0) + 1)
    );
  });
  const top3 = [...reasonCount.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  // Automation coverage
  const automated = data.filter((d) => !!d.result_json).length;
  console.log(automated);
  const automationCoverage = total ? Math.round((automated / total) * 100) : 0;

  // Delayed payments due to mismatch = exception records not ready for payment
  const delayed = per.filter((x) => x.hasException && !x.readyForPayment);
  const delayedCount = delayed.length;
  const delayedValue = delayed.reduce((s, x) => s + (x.invAmount || 0), 0);

  // Manual effort saved (FTEs/month) - estimated calculation

  const manualEffortSaved = (automated / 100).toFixed(1);

  // Average matching time (minutes): not present in sample; expose as N/A

  const avgMatchingTime = null;

  return {
    totals: {
      approved,
      total,
      fully,
      partial,
      withExceptions,
      readyPay,
      avgMatchingTime,
      automationCoverage,
      totalExceptionValue,
      agingCount,
      delayedCount,
      delayedValue,
      manualEffortSaved,
    },
    top3,
    per,
  };
}

/** ---------------- UI Components ---------------- */
function StatCard({ title, value, subtitle, icon, color, progress, loading }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (loading) {
    return (
      <Card
        elevation={1}
        sx={{
          height: "100%",
          borderRadius: 3,
          p: 3,
          background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
          border: "none",
        }}
      >
        <Skeleton variant="text" width="60%" height={28} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="40%" height={36} />
        {progress !== undefined && (
          <Box mt={2}>
            <Skeleton variant="rounded" height={8} sx={{ borderRadius: 4 }} />
          </Box>
        )}
      </Card>
    );
  }

  return (
    <Card
      elevation={2}
      sx={{
        height: "100%",
        borderRadius: 3,
        background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
        border: "none",
        display: "flex",
        flexDirection: "column",
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          background: "linear-gradient(135deg, #ffffff 0%, #f0f4ff 100%)",
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 3, position: "relative" }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={2}
        >
          <Box flexGrow={1}>
            <Typography
              variant="h5"
              color="text.secondary"
              gutterBottom
              fontWeight="600"
              fontSize={isMobile ? "0.9rem" : "2rem"}
              // sx={{ opacity: 0.8 }}
            >
              {title}
            </Typography>
            <Typography
              variant="h4"
              fontWeight="800"
              color={color}
              fontSize={isMobile ? "1.8rem" : "2.2rem"}
              lineHeight={1.1}
              sx={{
                textShadow: "0 2px 4px rgba(0,0,0,0.05)",
                mb: 1,
              }}
            >
              {value}
            </Typography>
            {subtitle && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontWeight: 500,
                  fontSize: isMobile ? "0.8rem" : "1.4rem",
                  display: "block",
                  opacity: 0.8,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              color,
              ml: 1,
              display: "flex",
              alignItems: "center",
              background: `${color}15`,
              borderRadius: "50%",
              p: 1.5,
            }}
          >
            {React.cloneElement(icon, {
              sx: {
                fontSize: isMobile ? "1.4rem" : "2rem",
                opacity: 0.9,
              },
            })}
          </Box>
        </Box>
        {progress !== undefined && (
          <Box mt={2.5}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: "grey.100",
                "& .MuiLinearProgress-bar": {
                  background: `linear-gradient(90deg, ${color}80, ${color})`,
                  borderRadius: 4,
                },
              }}
            />
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mt={1}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                fontSize="1.2rem"
                fontWeight={500}
              >
                Progress
              </Typography>
              <Typography
                variant="caption"
                color={color}
                fontSize="1.2rem"
                fontWeight={600}
              >
                {progress}%
              </Typography>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function MismatchReasonsTable({ data, totals, loading }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const baseFont = {
    fontFamily: `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif`,
    color: theme.palette.text.primary,
  };

  // Calculate total count of all mismatches (sum of all reason counts)
  const totalMismatches = data.reduce((sum, [, count]) => sum + count, 0);

  if (loading) {
    return (
      <Paper
        elevation={4}
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 3,
          background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
          ...baseFont,
        }}
      >
        <Skeleton variant="text" width="40%" height={38} sx={{ mb: 3 }} />
        <Divider sx={{ mb: 3, borderColor: "rgba(0,0,0,0.08)" }} />
        {[...Array(3)].map((_, i) => (
          <Box
            key={i}
            sx={{
              mb: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Skeleton variant="rounded" width="60%" height={28} />
            <Skeleton variant="rounded" width="20%" height={28} />
            <Skeleton variant="rounded" width="15%" height={28} />
          </Box>
        ))}
      </Paper>
    );
  }

  return (
    <Paper
      elevation={6}
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 3,
        background: "#ffffff",
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        overflow: "hidden",
        ...baseFont,
      }}
    >
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <Typography
          variant="h4"
          fontWeight={700}
          flexGrow={1}
          fontSize={isMobile ? "1.4rem" : "1.7rem"}
          letterSpacing="-0.2px"
        >
          Top Mismatch Reasons
        </Typography>
        <Tooltip
          title={`Most common reasons for invoice mismatches. Based on ${totalMismatches} total mismatches.`}
          slotProps={{
            tooltip: {
              sx: {
                fontSize: "1.1rem",
                borderRadius: 1,
                fontFamily: baseFont.fontFamily,
              },
            },
          }}
        >
          <IconButton
            size="medium"
            sx={{
              background: "rgba(0,0,0,0.05)",
              borderRadius: 2,
              "&:hover": {
                background: "rgba(0,0,0,0.1)",
                transform: "scale(1.05)",
              },
              transition: "all 0.2s ease",
            }}
          >
            <InfoOutlined fontSize="medium" />
          </IconButton>
        </Tooltip>
      </Box>

      <Divider sx={{ mb: 3, borderColor: "rgba(0,0,0,0.08)" }} />

      {/* Table */}
      <TableContainer sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Table
          size="medium"
          sx={{ borderCollapse: "separate", borderSpacing: "0 8px" }}
        >
          <TableHead>
            <TableRow>
              {["Reason", "Count", "Percentage"].map((heading, idx) => (
                <TableCell
                  key={idx}
                  align={idx === 0 ? "left" : "right"}
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 600,
                    color: "text.secondary",
                    py: 2.5,
                    px: 2,
                    backgroundColor: "rgba(0,0,0,0.02)",
                    border: "none",
                    letterSpacing: "0.3px",
                  }}
                >
                  {heading}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={3}
                  align="center"
                  sx={{
                    fontSize: "1.2rem",
                    py: 4,
                    color: "text.secondary",
                    fontStyle: "italic",
                    backgroundColor: "rgba(0,0,0,0.02)",
                    borderRadius: 2,
                  }}
                >
                  No mismatches found
                </TableCell>
              </TableRow>
            ) : (
              data.map(([reason, count], index) => {
                // FIXED: Calculate percentage based on total mismatches
                const percentage =
                  totalMismatches > 0
                    ? Math.round((count / totalMismatches) * 100)
                    : 0;

                return (
                  <TableRow
                    key={index}
                    hover
                    sx={{
                      transition: "all 0.2s ease",
                      borderRadius: 2,
                      "&:hover": {
                        transform: "translateY(-1px)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      },
                      "& .MuiTableCell-root:first-of-type": {
                        borderTopLeftRadius: 8,
                        borderBottomLeftRadius: 8,
                      },
                      "& .MuiTableCell-root:last-child": {
                        borderTopRightRadius: 8,
                        borderBottomRightRadius: 8,
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        fontSize: "1.3rem",
                        fontWeight: 500,
                        py: 2.5,
                        px: 2,
                        backgroundColor: "rgba(0,0,0,0.02)",
                      }}
                    >
                      {reason}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontSize: "1.3rem",
                        fontWeight: 600,
                        py: 2.5,
                        px: 2,
                        backgroundColor: "rgba(0,0,0,0.02)",
                      }}
                    >
                      {count}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontSize: "1.3rem",
                        fontWeight: 700,
                        color: "primary.main",
                        py: 2.5,
                        px: 2,
                        backgroundColor: "rgba(0,0,0,0.02)",
                      }}
                    >
                      {percentage}%
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Explanation of the percentage calculation */}
      <Box
        sx={{
          mt: 2,
          p: 2,
          backgroundColor: "rgba(0,0,0,0.02)",
          borderRadius: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontStyle: "italic", color: "text.secondary" }}
        >
          Percentage values represent the proportion of each mismatch reason
          relative to the total number of mismatches ({totalMismatches}).
        </Typography>
      </Box>
    </Paper>
  );
}

function RecordsSnapshot({ data, per, loading, onViewDetails }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const baseFont = {
    fontFamily: `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif`,
    color: theme.palette.text.primary,
  };

  const toINR = (num) =>
    num?.toLocaleString("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    });

  if (loading) {
    return (
      <Paper
        elevation={4}
        sx={{
          p: 4,
          borderRadius: 3,
          background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
          ...baseFont,
        }}
      >
        <Skeleton variant="text" width="40%" height={48} sx={{ mb: 3 }} />
        <Divider sx={{ mb: 3 }} />
        {[...Array(5)].map((_, i) => (
          <Box
            key={i}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
              py: 1,
            }}
          >
            <Skeleton variant="rounded" width="15%" height={36} />
            <Skeleton variant="rounded" width="12%" height={36} />
            <Skeleton variant="rounded" width="15%" height={36} />
            <Skeleton variant="rounded" width="12%" height={36} />
            <Skeleton variant="rounded" width="20%" height={36} />
            <Skeleton variant="rounded" width="8%" height={36} />
          </Box>
        ))}
      </Paper>
    );
  }

  return (
    <Paper
      elevation={6}
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        borderRadius: 3,
        background: "#ffffff",
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        overflow: "hidden",
        ...baseFont,
      }}
    >
      {/* ✅ Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <Typography
          variant="h4"
          fontWeight="700"
          flexGrow={1}
          fontSize={isMobile ? "1.4rem" : "1.7rem"}
          letterSpacing="-0.2px"
        >
          Recent Invoices
        </Typography>
        <Chip
          size="medium"
          label="Latest 5"
          color="primary"
          variant="filled"
          sx={{
            fontWeight: 600,
            fontSize: "1.2rem",
            px: 2,
            py: 1,
            borderRadius: 2,
          }}
        />
      </Box>

      <Divider sx={{ mb: 3, borderColor: "rgba(0,0,0,0.08)" }} />

      {/* ✅ Table */}
      <TableContainer sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Table
          size="medium"
          sx={{ borderCollapse: "separate", borderSpacing: "0 8px" }}
        >
          <TableHead>
            <TableRow>
              {[
                "Match Code",
                "Status",
                "Classification",
                "Amount",
                "Mismatches",
                "Actions",
              ].map((header, i) => (
                <TableCell
                  key={i}
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 600,
                    py: 2,
                    px: 2,
                    backgroundColor: "rgba(0,0,0,0.02)",
                    border: "none",
                    color: "text.secondary",
                    letterSpacing: "0.3px",
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {data
              .map((item, i) => ({ rec: item, p: per[i] })) // combine data + per
              .sort((a, b) => {
                const numA = parseInt(a.rec.match_code.split("/").pop());
                const numB = parseInt(b.rec.match_code.split("/").pop());
                return numB - numA; // descending
              })
              .slice(0, 5)
              .map(({ rec, p }) => (
                <TableRow
                  key={rec.token}
                  hover
                  sx={{
                    transition: "all 0.2s ease",
                    borderRadius: 2,
                    "&:hover": {
                      transform: "translateY(-1px)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    },
                    "& .MuiTableCell-root:first-of-type": {
                      borderTopLeftRadius: 8,
                      borderBottomLeftRadius: 8,
                    },
                    "& .MuiTableCell-root:last-child": {
                      borderTopRightRadius: 8,
                      borderBottomRightRadius: 8,
                    },
                  }}
                >
                  {/* MATCH CODE */}
                  <TableCell
                    sx={{
                      fontSize: "1.3rem",
                      fontWeight: 600,
                      py: 2,
                      px: 2,
                      backgroundColor: "rgba(0,0,0,0.02)",
                    }}
                  >
                    {rec.match_code}
                  </TableCell>

                  {/* STATUS */}
                  <TableCell
                    sx={{ py: 2, px: 2, backgroundColor: "rgba(0,0,0,0.02)" }}
                  >
                    <Chip
                      label={rec.status}
                      variant="filled"
                      sx={{
                        fontWeight: 600,
                        fontSize: "1rem",
                        px: 1.5,
                        borderRadius: 1.5,
                        minWidth: 100,
                        color:
                          rec.status === "APPROVED"
                            ? "#fff"
                            : rec.status === "IN_REVIEW"
                            ? "#ed6c02"
                            : "#fff",
                        backgroundColor:
                          rec.status === "APPROVED"
                            ? "#47a34b"
                            : rec.status === "IN_REVIEW"
                            ? "#fff3e0"
                            : rec.status === "REJECTED"
                            ? "#ee4646ff"
                            : "#ec8735ff",
                      }}
                    />
                  </TableCell>

                  {/* CLASSIFICATION */}
                  <TableCell
                    sx={{ py: 2, px: 2, backgroundColor: "rgba(0,0,0,0.02)" }}
                  >
                    <Chip
                      label={p.classification}
                      color={
                        p.classification === "Fully Matched"
                          ? "success"
                          : p.classification === "Partially Matched"
                          ? "warning"
                          : "error"
                      }
                      variant="outlined"
                      sx={{
                        fontWeight: 600,
                        fontSize: "1.1rem",
                        px: 1.5,
                        borderRadius: 1.5,
                        borderWidth: 1.5,
                        minWidth: 120,
                      }}
                    />
                  </TableCell>

                  {/* AMOUNT */}
                  <TableCell
                    sx={{
                      fontSize: "1.3rem",
                      fontWeight: 700,
                      py: 2,
                      px: 2,
                      backgroundColor: "rgba(0,0,0,0.02)",
                    }}
                  >
                    {toINR(p.invAmount)}
                  </TableCell>

                  {/* MISMATCHES */}
                  <TableCell
                    sx={{ py: 2, px: 2, backgroundColor: "rgba(0,0,0,0.02)" }}
                  >
                    <Typography
                      noWrap
                      sx={{
                        fontSize: "1.3rem",
                        fontWeight: 500,
                        color: p.mismatches.length
                          ? "text.primary"
                          : "text.secondary",
                      }}
                    >
                      {p.mismatches.slice(0, 1).join(" • ") || "—"}
                      {p.mismatches.length > 1 ? " …" : ""}
                    </Typography>
                  </TableCell>

                  {/* ACTIONS */}
                  <TableCell
                    sx={{ py: 2, px: 2, backgroundColor: "rgba(0,0,0,0.02)" }}
                  >
                    <Tooltip title="View details">
                      <IconButton
                        onClick={() => onViewDetails(rec, p)}
                        sx={{
                          background: "rgba(0, 0, 0, 0.04)",
                          borderRadius: 2,
                          p: 1,
                          transition: "all 0.2s ease",
                          "&:hover": {
                            background: theme.palette.primary.main,
                            color: "white",
                            transform: "scale(1.05)",
                          },
                        }}
                      >
                        <Visibility fontSize="medium" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

function DetailDialog({ open, onClose, record, reconciliation }) {
  // console.log({ reconciliation.mismatches });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (!record || !reconciliation) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
          p: isMobile ? 1 : 2,
        },
      }}
    >
      {/* ---------- HEADER ---------- */}
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
          borderBottom: `1px solid ${theme.palette.divider}`,
          py: isMobile ? 2 : 3,
          px: isMobile ? 2 : 4,
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography
            variant="h4"
            fontWeight={700}
            fontSize={isMobile ? "1.4rem" : "1.8rem"}
          >
            Invoice Details: {record.match_code}
          </Typography>
          <Chip
            label={reconciliation.classification}
            color={
              reconciliation.classification === "Fully Matched"
                ? "success"
                : reconciliation.classification === "Partially Matched"
                ? "warning"
                : "error"
            }
            size="medium"
            sx={{
              fontWeight: 700,
              fontSize: isMobile ? "0.9rem" : "1rem",
              px: 1.5,
            }}
          />
        </Box>
      </DialogTitle>

      {/* ---------- CONTENT ---------- */}
      <DialogContent
        dividers
        sx={{ py: isMobile ? 3 : 4, px: isMobile ? 2.5 : 4 }}
      >
        <Grid container spacing={4}>
          {/* Status */}
          <Grid item xs={12} sm={6}>
            <Typography
              variant="h6"
              fontWeight={600}
              color="text.secondary"
              gutterBottom
              fontSize={isMobile ? "1.1rem" : "1.5rem"}
            >
              Status
            </Typography>
            <Typography
              variant="h5"
              fontWeight={700}
              gutterBottom
              fontSize={isMobile ? "1.2rem" : "1.4rem"}
            >
              {record.status}
            </Typography>
          </Grid>

          {/* Invoice Amount */}
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              color="text.secondary"
              gutterBottom
              fontSize={isMobile ? "1.1rem" : "1.5rem"}
            >
              Invoice Amount
            </Typography>
            <Typography
              variant="h4"
              fontWeight={700}
              color="primary"
              gutterBottom
              fontSize={isMobile ? "1.4rem" : "1.6rem"}
            >
              {toINR(reconciliation.invAmount)}
            </Typography>
          </Grid>

          {/* Mismatches */}
          <Grid item xs={12}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              color="text.secondary"
              gutterBottom
              fontSize={isMobile ? "1rem" : "1.5rem"}
            >
              Mismatches
            </Typography>
            {reconciliation.mismatches.length > 0 ? (
              <Box
                component="ul"
                sx={{
                  pl: 3,
                  m: 0,
                  background: "#fff3cd30",
                  border: "1px solid #ffeaa7",
                  borderRadius: 2,
                  p: 2,
                }}
              >
                {reconciliation.mismatches.map((mismatch, index) => (
                  <Typography
                    component="li"
                    key={index}
                    sx={{
                      mb: 1,
                      fontWeight: 500,
                      fontSize: isMobile ? "1rem" : "1.5rem",
                      lineHeight: 1.6,
                    }}
                  >
                    {mismatch}
                  </Typography>
                ))}
              </Box>
            ) : (
              <Typography
                variant="body1"
                sx={{
                  fontStyle: "italic",
                  color: "text.secondary",
                  fontSize: isMobile ? "1rem" : "1.1rem",
                }}
              >
                No mismatches found
              </Typography>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      {/* ---------- ACTIONS ---------- */}
      <DialogActions
        sx={{
          px: isMobile ? 2.5 : 4,
          py: 2.5,
          background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            borderRadius: 2,
            fontWeight: 700,
            px: 4,
            py: 1.2,
            fontSize: isMobile ? "1rem" : "1.1rem",
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function LoadingSkeleton() {
  return (
    <Box sx={{ p: 4, bgcolor: "#f8f9fa", minHeight: "100vh" }}>
      <Skeleton variant="text" width="40%" height={48} sx={{ mb: 3 }} />
      <Grid container spacing={3}>
        {[...Array(8)].map((_, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Skeleton variant="rounded" height={160} sx={{ borderRadius: 3 }} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

function getMonthlyTrends(per) {
  const monthly = {};

  per.forEach((p) => {
    if (!p.createdAt) return;
    const date = new Date(p.createdAt);
    const month = date.toLocaleString("en-US", { month: "short" });
    monthly[month] = monthly[month] || { name: month, invoices: 0, matched: 0 };
    monthly[month].invoices += 1;
    if (p.classification === "Fully Matched") monthly[month].matched += 1;
  });

  // Sort by actual month order
  const monthOrder = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  return monthOrder.filter((m) => monthly[m]).map((m) => monthly[m]);
}

function ErrorDisplay({ message, onRetry }) {
  return (
    <Box
      sx={{
        p: 4,
        textAlign: "center",
        bgcolor: "#f8f9fa",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <ErrorOutline
        sx={{
          fontSize: 64,
          color: "error.main",
          mb: 3,
          opacity: 0.8,
        }}
      />
      <Typography
        variant="h4"
        fontWeight="700"
        color="error"
        gutterBottom
        fontSize="1.5rem"
      >
        Something went wrong
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{
          mb: 3,
          maxWidth: 500,
          lineHeight: 1.6,
          fontSize: "1rem",
        }}
      >
        {message}
      </Typography>
      <Button
        variant="contained"
        onClick={onRetry}
        size="large"
        startIcon={<Refresh />}
        sx={{
          borderRadius: 2,
          fontWeight: 600,
          px: 4,
          py: 1.5,
          fontSize: "0.9rem",
        }}
      >
        Try Again
      </Button>
    </Box>
  );
}

function InvoiceTrendChart({ data, loading }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [trendData, setTrendData] = useState(null);

  // useEffect(() => {

  //   setTrendData([
  //     { name: "Jan", invoices: 1200, matched: 800 },
  //     { name: "Feb", invoices: 1900, matched: 1200 },
  //     { name: "Mar", invoices: 1500, matched: 1000 },
  //     { name: "Apr", invoices: 2100, matched: 1600 },
  //     { name: "May", invoices: 1800, matched: 1400 },
  //     { name: "Jun", invoices: 2300, matched: 1800 },
  //   ]);

  // }, []);

  useEffect(() => {
    if (data && data.length > 0) {
      const trends = getMonthlyTrends(data);
      setTrendData(trends);
    }
  }, [data]);

  if (loading || !trendData) {
    return (
      <Paper elevation={3} sx={{ p: 3, borderRadius: 3, height: 400 }}>
        <Skeleton variant="text" width="40%" height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
      </Paper>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        borderRadius: 3,
        height: 400,
        background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography
        variant="h5"
        fontWeight={700}
        gutterBottom
        fontSize={isMobile ? "1.3rem" : "1.5rem"}
      >
        Invoice Processing Trends
      </Typography>
      <Box sx={{ flexGrow: 1 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={trendData}
            margin={{ top: 10, right: 20, left: 10, bottom: 10 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={theme.palette.divider}
            />
            <XAxis
              dataKey="name"
              stroke={theme.palette.text.secondary}
              fontSize={isMobile ? 12 : 14}
              tick={{ fill: theme.palette.text.primary }}
            />
            <YAxis
              stroke={theme.palette.text.secondary}
              fontSize={isMobile ? 12 : 14}
              tick={{ fill: theme.palette.text.primary }}
            />
            <RechartsTooltip
              contentStyle={{
                borderRadius: 8,
                fontSize: isMobile ? "12px" : "14px",
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
              }}
            />
            <Legend
              wrapperStyle={{
                fontSize: isMobile ? "12px" : "14px",
                paddingTop: "10px",
              }}
            />
            <Line
              type="monotone"
              dataKey="invoices"
              stroke="#8884d8"
              strokeWidth={3}
              name="Total Invoices"
              dot={{ fill: "#8884d8", strokeWidth: 2, r: 4 }}
            />
            <Line
              type="monotone"
              dataKey="matched"
              stroke="#82ca9d"
              strokeWidth={3}
              name="Fully Matched"
              dot={{ fill: "#82ca9d", strokeWidth: 2, r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
}

// function MismatchDonutChart({ data = [], loading }) {
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
//   const [chartData, setChartData] = useState(null);

//   // 🌈 pastel vibrant palette
//   const CHART_COLORS = [
//     "#4F46E5", // Indigo
//     "#06B6D4", // Cyan
//     "#22C55E", // Green
//     "#F59E0B", // Amber
//     "#EF4444", // Red
//     "#EC4899", // Pink
//     "#8B5CF6", // Violet
//     "#3B82F6", // Blue
//   ];

//   useEffect(() => {
//     // ---- if you had an API call it would go here ----
//     if (!data || !Array.isArray(data)) return;

//     // Handle both [["name", value]] and [{name,value}]
//     const formatted = data.map((item) => {
//       if (Array.isArray(item)) {
//         const [name, value] = item;
//         return { name, value: Number(value) };
//       } else {
//         return { name: item.name, value: Number(item.value) };
//       }
//     });

//     setChartData(formatted);
//   }, [data]);

//   if (loading || !chartData) {
//     return (
//       <Paper
//         elevation={3}
//         sx={{
//           p: 3,
//           borderRadius: 3,
//           height: 400,
//           display: "flex",
//           flexDirection: "column",
//           justifyContent: "center",
//           background: "linear-gradient(135deg, #ffffff 0%, #f5f7fa 100%)",
//         }}
//       >
//         <Skeleton
//           variant="text"
//           width="70%"
//           height={50}
//           sx={{ mb: 3, mx: "auto", bgcolor: "grey.200" }}
//         />
//         <Skeleton
//           variant="circular"
//           width={250}
//           height={250}
//           sx={{ mx: "auto", bgcolor: "grey.200" }}
//         />
//       </Paper>
//     );
//   }

//   const totalValue = chartData.reduce((sum, item) => sum + item.value, 0);

//   const CustomTooltip = ({ active, payload }) => {
//     if (active && payload?.length) {
//       const { name, value } = payload[0];
//       const percentage = ((value / totalValue) * 100).toFixed(1);
//       return (
//         <Paper
//           elevation={4}
//           sx={{
//             p: 2,
//             borderRadius: 2,
//             backgroundColor: "#ffffff",
//             border: `1px solid ${theme.palette.divider}`,
//             minWidth: 180,
//           }}
//         >
//           <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
//             {name}
//           </Typography>
//           <Typography variant="body1">Count: {value}</Typography>
//           <Typography variant="body2" color="text.secondary">
//             {percentage}% of total
//           </Typography>
//         </Paper>
//       );
//     }
//     return null;
//   };

//   const renderCustomizedLabel = ({
//     cx,
//     cy,
//     midAngle,
//     innerRadius,
//     outerRadius,
//     percent,
//   }) => {
//     const RADIAN = Math.PI / 180;
//     const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
//     const x = cx + radius * Math.cos(-midAngle * RADIAN);
//     const y = cy + radius * Math.sin(-midAngle * RADIAN);
//     if (percent < 0.05) return null;

//     return (
//       <text
//         x={x}
//         y={y}
//         fill={theme.palette.text.primary}
//         textAnchor={x > cx ? "start" : "end"}
//         dominantBaseline="central"
//         fontSize={12}
//         fontWeight="600"
//       >
//         {`${(percent * 100).toFixed(0)}%`}
//       </text>
//     );
//   };

//   return (
//     <Paper
//       elevation={4}
//       sx={{
//         p: 3,
//         borderRadius: 3,
//         height: 400,
//         background: "linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)",
//         border: `1px solid ${theme.palette.divider}`,
//         boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
//         display: "flex",
//         flexDirection: "column",
//       }}
//     >
//       <Typography
//         variant="h5"
//         fontWeight={700}
//         textAlign="center"
//         gutterBottom
//         fontSize={isMobile ? "1.3rem" : "1.5rem"}
//         sx={{ color: theme.palette.primary.main }}
//       >
//         Mismatch Reasons Distribution
//       </Typography>

//       <Box sx={{ flexGrow: 1, position: "relative" }}>
//         <ResponsiveContainer width="100%" height="100%">
//           <PieChart>
//             <Pie
//               data={chartData}
//               cx="50%"
//               cy="50%"
//               labelLine={false}
//               outerRadius={isMobile ? 90 : 100}
//               innerRadius={isMobile ? 60 : 70}
//               dataKey="value"
//               label={renderCustomizedLabel}
//               paddingAngle={2}
//             >
//               {chartData.map((entry, index) => (
//                 <Cell
//                   key={`cell-${index}`}
//                   fill={CHART_COLORS[index % CHART_COLORS.length]}
//                   stroke="#ffffff"
//                   strokeWidth={2}
//                 />
//               ))}
//             </Pie>
//             <Tooltip content={<CustomTooltip />} />
//           </PieChart>
//         </ResponsiveContainer>

//         {/* Center Total */}
//         <Box
//           sx={{
//             position: "absolute",
//             top: "50%",
//             left: "50%",
//             transform: "translate(-50%,-50%)",
//             textAlign: "center",
//             pointerEvents: "none",
//           }}
//         >
//           <Typography
//             variant="body2"
//             fontWeight="600"
//             color="text.secondary"
//             fontSize="0.9rem"
//           >
//             Total
//           </Typography>
//           <Typography
//             variant="h6"
//             fontWeight="800"
//             color="text.primary"
//             fontSize="1.4rem"
//           >
//             {totalValue.toLocaleString()}
//           </Typography>
//         </Box>
//       </Box>

//       {/* Legend */}
//       <Box
//         sx={{
//           display: "flex",
//           flexDirection: "column",
//           gap: 1,
//           mt: 2,
//           pt: 2,
//           borderTop: `1px solid ${theme.palette.divider}`,
//           maxHeight: 90,
//           overflowY: "auto",
//           "&::-webkit-scrollbar": { width: 5 },
//           "&::-webkit-scrollbar-thumb": {
//             background: theme.palette.primary.light,
//             borderRadius: 3,
//           },
//         }}
//       >
//         {chartData.map((entry, index) => (
//           <Box
//             key={index}
//             sx={{
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "space-between",
//             }}
//           >
//             <Box sx={{ display: "flex", alignItems: "center" }}>
//               <Box
//                 sx={{
//                   width: 12,
//                   height: 12,
//                   borderRadius: "50%",
//                   backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
//                   mr: 1.5,
//                 }}
//               />
//               <Typography
//                 variant="body2"
//                 fontSize="0.85rem"
//                 color="text.primary"
//                 sx={{
//                   maxWidth: 140,
//                   overflow: "hidden",
//                   textOverflow: "ellipsis",
//                   whiteSpace: "nowrap",
//                 }}
//               >
//                 {entry.name}
//               </Typography>
//             </Box>
//             <Typography
//               variant="body2"
//               fontSize="0.85rem"
//               color="text.secondary"
//             >
//               {((entry.value / totalValue) * 100).toFixed(0)}%
//             </Typography>
//           </Box>
//         ))}
//       </Box>
//     </Paper>
//   );
// }

function MismatchPieChart({ data, loading }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const [chartData, setChartData] = useState(null);
  const [activeIndex, setActiveIndex] = useState(null);

  // Fetch API or fallback to props
  useEffect(() => {
    setChartData(data.map(([name, value]) => ({ name, value })));
  }, [data]);

  const PIE_COLORS = [
    "#EF5350",
    "#42A5F5",
    "#FFA726",
    "#66BB6A",
    "#AB47BC",
    "#26C6DA",
    "#FFEE58",
    "#8D6E63",
  ];

  const onPieEnter = (_, index) => setActiveIndex(index);
  const onPieLeave = () => setActiveIndex(null);

  // Responsive dimensions
  const getContainerHeight = () => {
    if (isMobile) return 550;
    if (isTablet) return 500;
    return 450;
  };

  const getOuterRadius = () => {
    if (isMobile) return 90;
    if (isTablet) return 105;
    return 115;
  };

  const getInnerRadius = () => {
    if (isMobile) return 55;
    if (isTablet) return 65;
    return 75;
  };

  const getTitleSize = () => {
    if (isMobile) return "1.3rem";
    if (isTablet) return "1.5rem";
    return "1.6rem";
  };

  if (loading || !chartData) {
    return (
      <Paper
        elevation={2}
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 4,
          height: getContainerHeight(),
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Skeleton
          variant="text"
          width={isMobile ? "90%" : "70%"}
          height={40}
          sx={{ mb: 3, bgcolor: "grey.300", borderRadius: 2 }}
        />
        <Skeleton
          variant="circular"
          width={isMobile ? 180 : 220}
          height={isMobile ? 180 : 220}
          sx={{ mb: 2, bgcolor: "grey.300" }}
        />
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: 1,
            mt: 2,
          }}
        >
          {[1, 2, 3, 4].map((item) => (
            <Skeleton
              key={item}
              variant="rounded"
              width={isMobile ? 70 : 80}
              height={20}
              sx={{ bgcolor: "grey.300", borderRadius: 4 }}
            />
          ))}
        </Box>
      </Paper>
    );
  }

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  // Custom label inside slices in black
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
  }) => {
    if (percent < 0.03) return null; // hide tiny slices

    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.65;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="#000"
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        fontSize={isMobile ? 10 : 12}
        fontWeight={700}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  // Small tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const item = payload[0];
      const percentage = ((item.value / total) * 100).toFixed(1);

      return (
        <Paper
          elevation={4}
          sx={{
            p: 1.5,
            borderRadius: 2,
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            border: `1px solid ${item.payload.fill}`,
            minWidth: 140,
            maxWidth: 180,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: item.payload.fill,
                mr: 1,
              }}
            />
            <Typography variant="h6" fontWeight="bold">
              {item.name}
            </Typography>
          </Box>
          <Typography variant="h6">
            Count: {item.value.toLocaleString()}
          </Typography>
          <Typography variant="h6" color="text.secondary">
            {percentage}% of total
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  return (
    <Paper
      elevation={2}
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 4,
        height: getContainerHeight(),
        background: "linear-gradient(135deg, #ffffff 0%, #fafafa 100%)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
        border: `1px solid ${theme.palette.divider}`,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Typography
        variant="h5"
        fontWeight={700}
        textAlign="center"
        gutterBottom
        sx={{
          background: "linear-gradient(45deg, #D32F2F 30%, #303F9F 90%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
          fontSize: getTitleSize(),
          mb: 2,
        }}
      >
        Mismatch Reasons Distribution
      </Typography>

      <Box sx={{ position: "relative", height: { xs: 320, sm: 350, md: 380 } }}>
        <ResponsiveContainer width="100%" height="100%">
          <RechartsPieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              outerRadius={getOuterRadius()}
              innerRadius={getInnerRadius()}
              dataKey="value"
              labelLine={false}
              label={renderCustomizedLabel}
              activeIndex={activeIndex}
              onMouseEnter={onPieEnter}
              onMouseLeave={onPieLeave}
              paddingAngle={2}
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={PIE_COLORS[index % PIE_COLORS.length]}
                  stroke="#fff"
                  strokeWidth={2}
                  opacity={
                    activeIndex === null || activeIndex === index ? 1 : 0.7
                  }
                />
              ))}
            </Pie>
            <RechartsTooltip
              content={<CustomTooltip />}
              wrapperStyle={{ zIndex: 9999 }}
            />
          </RechartsPieChart>
        </ResponsiveContainer>

        {/* Total in center */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            pointerEvents: "none",
            zIndex: 0,
          }}
        >
          <Typography
            variant={isMobile ? "body2" : "h5"}
            color="text.secondary"
            fontWeight={600}
          >
            Total
          </Typography>
          <Typography
            variant={isMobile ? "h6" : "h5"}
            color="primary"
            fontWeight={800}
          >
            {total.toLocaleString()}
          </Typography>
        </Box>
      </Box>

      {/* Custom Legend */}
      <Box
        sx={{
          mt: 2,
          pt: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          maxHeight: 100,
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.primary.light,
            borderRadius: 3,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: 1,
          }}
        >
          {chartData.map((entry, index) => {
            const percentage = ((entry.value / total) * 100).toFixed(1);
            return (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor:
                    activeIndex === index ? "action.hover" : "transparent",
                  borderRadius: 4,
                  px: 1,
                  py: 0.5,
                  transition: "background-color 0.2s",
                  cursor: "pointer",
                  maxWidth: isMobile ? "45%" : "auto",
                  flex: isMobile ? "1 0 45%" : "auto",
                }}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(null)}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: PIE_COLORS[index % PIE_COLORS.length],
                    mr: 1,
                    border: `1px solid ${theme.palette.common.white}`,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 500,
                    mr: 0.5,
                    color: "text.primary",
                    // overflow: "hidden",
                    // textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {entry.name}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    flexShrink: 0,
                  }}
                >
                  ({percentage}%)
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Paper>
  );
}

function NavigationDrawer({ open, onClose, themeMode, toggleTheme }) {
  return (
    <Drawer
      variant="temporary"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        display: { xs: "block", md: "none" },
        "& .MuiDrawer-paper": {
          boxSizing: "border-box",
          width: 280,
          background: "linear-gradient(180deg, #f8f9fa 0%, #e9ecef 100%)",
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          Invoice Dashboard
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <List>
          <ListItem button>
            <ListItemIcon>
              <Dashboard />
            </ListItemIcon>
            <ListItemText primary="Dashboard" />
          </ListItem>
          <ListItem button>
            <ListItemIcon>
              <BarChart />
            </ListItemIcon>
            <ListItemText primary="Analytics" />
          </ListItem>
          <ListItem button>
            <ListItemIcon>
              <Receipt />
            </ListItemIcon>
            <ListItemText primary="Invoices" />
          </ListItem>
          <ListItem button>
            <ListItemIcon>
              <PieChart />
            </ListItemIcon>
            <ListItemText primary="Reports" />
          </ListItem>
        </List>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: "flex", alignItems: "center", px: 2 }}>
          <Brightness4 />
          <Switch
            checked={themeMode === "dark"}
            onChange={toggleTheme}
            sx={{ mx: 1 }}
          />
          <Brightness7 />
        </Box>
      </Box>
    </Drawer>
  );
}

export default function TWMOverviewDashboard({ data = [] }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isLoading, setIsLoading] = useState(!data.length);
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [themeMode, setThemeMode] = useState("light");

  const [detailDialog, setDetailDialog] = useState({
    open: false,
    record: null,
    reconciliation: null,
  });

  const { totals, top3, per } = useMemo(() => {
    try {
      return computeKPIs(data);
    } catch (err) {
      setError(err.message);
      return { totals: {}, top3: [], per: [] };
    }
  }, [data]);

  useEffect(() => {
    if (data.length) {
      setIsLoading(false);
      setError(null);
    }
  }, [data]);

  const handleRefresh = () => {
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 1000);
  };

  const handleExport = () => {
    console.log("Exporting data...");
  };

  const handleViewDetails = (record, reconciliation) => {
    setDetailDialog({ open: true, record, reconciliation });
  };

  const handleCloseDetails = () => {
    setDetailDialog({ open: false, record: null, reconciliation: null });
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const toggleTheme = () => {
    setThemeMode(themeMode === "light" ? "dark" : "light");
  };

  if (isLoading && !data.length) return <LoadingSkeleton />;
  if (error)
    return (
      <ErrorDisplay message={error} onRetry={() => window.location.reload()} />
    );

  return (
    <Box
      sx={{
        display: "flex",
        bgcolor: "#f4f6f8",
        minHeight: "100vh",
        width: "100%",
      }}
    >
      {/* Mobile Navigation Drawer */}
      <NavigationDrawer
        open={drawerOpen}
        onClose={toggleDrawer}
        themeMode={themeMode}
        toggleTheme={toggleTheme}
      />

      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1, p: isMobile ? 2 : 3 }}>
        {/* 🔹 Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={4}
          flexDirection={isMobile ? "column" : "row"}
          gap={isMobile ? 2 : 0}
        >
          <Box>
            <Typography
              variant="h4"
              fontWeight="800"
              color="primary"
              gutterBottom
              fontSize={isMobile ? "1.8rem" : "2.6rem"}
            >
              Invoice Process Automation Dashboard
            </Typography>
            <Typography
              variant="h5"
              color="text.secondary"
              fontSize={isMobile ? "1rem" : "1.4rem"}
              fontWeight={400}
            >
              Monitor and analyze invoice matching performance
            </Typography>
          </Box>
          <Box display="flex" gap={2} mt={isMobile ? 2 : 0}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              size="large"
              sx={{ borderRadius: 2, fontWeight: 600, fontSize: "1.2rem" }}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* 🔹 Key Metrics Grid */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Invoices Processed"
              value={totals.total}
              icon={<TrendingUp fontSize="large" />}
              color={theme.palette.primary.main}
              loading={isLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Fully Matched Invoices"
              value={totals.fully}
              subtitle={`${Math.round(
                (totals.fully / totals.total) * 100
              )}% of total`}
              icon={<CheckCircle fontSize="large" />}
              color={theme.palette.success.main}
              // progress={Math.round((totals.fully / totals.total) * 100)}
              loading={isLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Partially Matched Invoices"
              value={totals.partial}
              icon={<Autorenew fontSize="large" />}
              color={theme.palette.warning.main}
              loading={isLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Invoices with Exceptions"
              value={totals.withExceptions}
              icon={<Warning fontSize="large" />}
              color={theme.palette.error.main}
              loading={isLoading}
            />
          </Grid>
        </Grid>

        {/* 🔹 Second Row */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Ready for Payment"
              value={totals.approved}
              icon={<Payment fontSize="large" />}
              color={theme.palette.info.main}
              loading={isLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Average Matching Time"
              value={
                totals.avgMatchingTime != null
                  ? `${totals.avgMatchingTime} mins`
                  : "N/A"
              }
              icon={<Schedule fontSize="large" />}
              color={theme.palette.primary.light}
              loading={isLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Automation Coverage"
              value={`${totals.automationCoverage}%`}
              icon={<AssignmentTurnedIn fontSize="large" />}
              color={theme.palette.success.light}
              progress={totals.automationCoverage}
              loading={isLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Exception Value"
              value={toINR(totals.totalExceptionValue)}
              icon={<ErrorOutline fontSize="large" />}
              color={theme.palette.error.light}
              loading={isLoading}
            />
          </Grid>
        </Grid>

        {/* 🔹 Third Row */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Manual Effort Saved"
              value={`${totals.manualEffortSaved} FTEs/month`}
              icon={<Person fontSize="large" />}
              color={theme.palette.info.light}
              loading={isLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Aging > 7 Days"
              value={totals.agingCount}
              icon={<AccessTime fontSize="large" />}
              color={theme.palette.warning.dark}
              loading={isLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Delayed Payments"
              value={totals.delayedCount}
              subtitle={`${toINR(totals.delayedValue)} value`}
              icon={<PendingActions fontSize="large" />}
              color={theme.palette.error.dark}
              loading={isLoading}
            />
          </Grid>

          {/* Exception Resolution Rate */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                borderRadius: 3,
                background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                height: "100%",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                  background:
                    "linear-gradient(135deg, #ffffff 0%, #f0f4ff 100%)",
                },
              }}
            >
              <Box display="flex" alignItems="center" mb={2}>
                <Typography
                  variant="h6"
                  fontWeight="700"
                  flexGrow={1}
                  color="text.secondary"
                  fontSize="2rem"
                >
                  Exception Resolution Rate
                </Typography>
                <Tooltip
                  title="Percentage of exceptions resolved within 7 days"
                  slotProps={{ tooltip: { sx: { fontSize: "1.2rem" } } }}
                >
                  <IconButton
                    size="small"
                    sx={{
                      background: "rgba(0,0,0,0.05)",
                      "&:hover": { background: "rgba(0,0,0,0.08)" },
                    }}
                  >
                    <InfoOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography
                variant="h4"
                fontWeight="800"
                color={theme.palette.success.main}
                fontSize="2.2rem"
              >
                {totals.withExceptions > 0
                  ? `${Math.round(
                      ((totals.withExceptions - totals.agingCount) /
                        totals.withExceptions) *
                        100
                    )}%`
                  : "100%"}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                fontWeight={500}
                mt={1}
                fontSize="1.4rem"
              >
                Resolved within 7 days
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* 🔹 Charts Section */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={8}>
            <InvoiceTrendChart data={per} loading={isLoading} />
          </Grid>
          <Grid item xs={12} md={4}>
            <MismatchPieChart data={top3} loading={isLoading} />
          </Grid>
        </Grid>

        {/* 🔹 Charts and Tables */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={8}>
            <MismatchReasonsTable
              data={top3}
              totals={totals}
              loading={isLoading}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Stack spacing={3}>
              {/* Aging Exceptions Card */}
              <Paper
                elevation={6}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  background: "#ffffff",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                  position: "relative",
                  overflow: "hidden",
                  "&:before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "4px",
                    background:
                      "linear-gradient(90deg, #ff6b6b 0%, #ff8e8e 100%)",
                  },
                }}
              >
                <Box display="flex" alignItems="center" mb={2}>
                  <Typography
                    variant="h5"
                    fontWeight="700"
                    flexGrow={1}
                    // fontSize="1.2rem"
                    fontFamily="'Inter', sans-serif"
                  >
                    Aging Exceptions
                  </Typography>
                  <Chip
                    label={`${totals.agingCount} cases`}
                    color="error"
                    variant="filled"
                    sx={{
                      fontWeight: 700,
                      fontSize: "1.1rem",
                      borderRadius: 2,
                      px: 1.5,
                      py: 1,
                      background:
                        "linear-gradient(135deg, #ff6b6b 0%, #ff8e8e 100%)",
                      boxShadow: "0 2px 8px rgba(255,107,107,0.3)",
                    }}
                  />
                </Box>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  fontSize="1.3rem"
                  fontFamily="'Inter', sans-serif"
                  mb={2}
                >
                  Cases pending beyond 7 days
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mt: 1,
                  }}
                >
                  <AccessTime
                    fontSize="small"
                    sx={{ color: "error.main", mr: 1 }}
                  />
                  <Typography
                    variant="body1"
                    color="error.main"
                    fontSize="1.2rem"
                    fontWeight="600"
                    fontFamily="'Inter', sans-serif"
                  >
                    Requires immediate attention
                  </Typography>
                </Box>
              </Paper>

              {/* Delayed Payments Card */}
              <Paper
                elevation={6}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  background: "#ffffff",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                  position: "relative",
                  overflow: "hidden",
                  "&:before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "4px",
                    background:
                      "linear-gradient(90deg, #ff9800 0%, #ffb74d 100%)",
                  },
                }}
              >
                <Box display="flex" alignItems="center" mb={2}>
                  <Typography
                    variant="h5"
                    fontWeight="700"
                    flexGrow={1}
                    // fontSize="1.2rem"
                    fontFamily="'Inter', sans-serif"
                  >
                    Delayed Payments
                  </Typography>
                  <Chip
                    label={`${totals.delayedCount} invoices`}
                    color="warning"
                    variant="filled"
                    sx={{
                      fontWeight: 700,
                      fontSize: "1.1rem",
                      borderRadius: 2,
                      px: 1.5,
                      py: 1,
                      background:
                        "linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)",
                      boxShadow: "0 2px 8px rgba(255,152,0,0.3)",
                    }}
                  />
                </Box>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  gutterBottom
                  fontSize="1.3rem"
                  fontFamily="'Inter', sans-serif"
                  mb={2}
                >
                  Due to unresolved mismatches
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "baseline",
                    mt: 2,
                  }}
                >
                  <Typography
                    variant="h4"
                    fontWeight="800"
                    color="warning.dark"
                    fontSize="1.8rem"
                    fontFamily="'Inter', sans-serif"
                  >
                    {toINR(totals.delayedValue)}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    fontSize="1.2rem"
                    ml={1}
                    fontFamily="'Inter', sans-serif"
                  >
                    pending
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mt: 1.5,
                  }}
                >
                  <WarningAmber
                    fontSize="small"
                    sx={{ color: "warning.main", mr: 1 }}
                  />
                  <Typography
                    variant="body1"
                    color="warning.dark"
                    fontWeight="600"
                    fontSize="1.2rem"
                    fontFamily="'Inter', sans-serif"
                  >
                    Resolve mismatches to release payments
                  </Typography>
                </Box>
              </Paper>
            </Stack>
          </Grid>
        </Grid>

        {/* 🔹 Records Snapshot */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <RecordsSnapshot
              data={data}
              per={per}
              loading={isLoading}
              onViewDetails={handleViewDetails}
            />
          </Grid>
        </Grid>

        {/* 🔹 Detail Dialog */}
        <DetailDialog
          open={detailDialog.open}
          onClose={handleCloseDetails}
          record={detailDialog.record}
          reconciliation={detailDialog.reconciliation}
        />
      </Box>
    </Box>
  );
}
